import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import { showErrorMessage } from "../../shared/Services/toasterService";
import "../../assets/scss/styles.scss";
import { useLocalization } from "../../context/LocalizationContext";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGO_URL = process.env.REACT_APP_LOGIN_LOGO_URL || "/assets/images/logo/AutoMenuLogo.png";
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t: localization } = useLocalization();
  const prefillEmail = searchParams.get("email") || "";
  const isEmailPrefilled = Boolean(prefillEmail);

  const [email, setEmail] = useState(prefillEmail);
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, [prefillEmail]);

  const validate = () => {
    if (!email.trim()) {
      setEmailError("Email is required.");
      return false;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError("Please enter a valid email address.");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_FORGOT_PASSWORD,
        { email: email.trim() },
      );
      if (response.status === 200) {
        setLinkSent(true);
      } else {
        showErrorMessage("Unable to send reset link. Please try again.");
      }
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <div className="loginwrapper">
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-logo">
          <img
              className="fusz-logo"
              src={LOGO_URL}
              alt={localization("lou_fusz_logo_alt")}
          />
        </div>

        <h2>Forgot Password?</h2>
        <p className="auth-subtitle">
          Enter your registered email address and we'll send you a link to reset
          your password.
        </p>

        {linkSent ? (
          <div className="auth-success-message">
            <strong>Check your email!</strong>
            <br />A password reset link has been sent to{" "}
            <strong>{email}</strong>. Please follow the instructions in the
            email to reset your password.
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSendLink} noValidate>
            <div className="form-group">
              <label htmlFor="forgot-email">Email</label>
              <div className="input-icon-wrapper">
                <i className="fas fa-envelope input-icon" />
                <input
                  id="forgot-email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  disabled={isEmailPrefilled}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  autoComplete="email"
                />
              </div>
              {emailError && <p className="field-error">{emailError}</p>}
            </div>

            <div className="auth-actions">
              <button
                type="submit"
                className="btn-primary-action"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </button>
              <button
                type="button"
                className="btn-cancel-action"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {linkSent && (
          <div className="auth-actions" style={{ marginTop: "16px" }}>
            <button
              type="button"
              className="btn-cancel-action"
              onClick={handleCancel}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
